import { UoMTypesEnum } from '#uom';
import { z } from 'zod';

export const editQuoteParameters = z.object({
  createQuoteSections: z.array(z.object({
    name: z.string().describe('Name of the quote section'),
    description: z.string().optional().describe('Description of the quote section'),
    order: z.number().describe('Order of the quote section'),
  })).optional(),
  editQuoteSections: z.array(z.object({
    quote_section_id: z.number().describe('ID of the quote section to edit'),
    name: z.string().optional().describe('New name of the quote section'),
    description: z.string().optional().describe('New description of the quote section'),
    order: z.number().optional().describe('New order of the quote section'),
  })).optional(),
  createQuoteItemDatabaseObjects: z.array(z.object({
    database_object_id: z.number().describe('ID of the database object to add'),
    quote_section_id: z.union([z.number(), z.string()]).describe('ID of the quote section to add the database object to. If the section was created in the same step, use the section\'s name'),
    margin: z.number().default(0.1).describe('Margin to apply to the database object'),
    data: z.array(z.object({
      id: z.string().describe('The attribute UUID, not the display name'),
      value: z.number().describe('Numeric value for the attribute'),
      unit: z.nativeEnum(UoMTypesEnum).describe('Unit of measurement'),
    })).describe('Array of variable attribute objects for the database object. Should always have at least quantity attribute (id quantity in all languages)'),
    order: z.number().describe('Order of the database object in the quote section'),
  })).optional(),
  editQuoteItemDatabaseObjects: z.array(z.object({
    quote_item_database_object_id: z.number().describe('ID of the quote item database object to edit'),
    margin: z.number().optional().describe('New margin to apply to the database object'),
    data: z.array(z.object({
      id: z.string().describe('The attribute UUID, not the display name'),
      value: z.number().describe('Numeric value for the attribute'),
      unit: z.nativeEnum(UoMTypesEnum).describe('Unit of measurement'),
    })).optional().describe('Complete array of all variable attribute objects for the database object. Should always have at least quantity attribute (id quantity in all languages)'),
    order: z.number().optional().describe('New order of the database object in the quote section'),
  })).optional(),
  createQuoteItemModules: z.array(z.object({
    module_id: z.number().describe('ID of the module to add'),
    quote_section_id: z.union([z.number(), z.string()]).describe('ID of the quote section to add the module to. If the section was created in the same step, use the section\'s name'),
    margin: z.number().default(0.1).describe('Margin to apply to the module (default: 0.1)'),
    data: z.array(z.object({
      id: z.string().describe('The UUID not the display name'),
      value: z.number().describe('Numeric value for the attribute'),
      unit: z.nativeEnum(UoMTypesEnum).describe('Unit of measurement (e.g., "h" for hours)'),
    })).optional().describe('Array of variable attribute objects for the module. Should always have at least quantity attribute (id quantity in all languages)'),
    order: z.number().describe('Order of the module in the quote section'),
  })).optional(),
  editQuoteItemModules: z.array(z.object({
    quote_item_module_id: z.number().describe('ID of the quote item module to edit'),
    margin: z.number().optional().describe('New margin to apply to the module'),
    data: z.array(z.object({
      id: z.string().describe('The UUID not the display name'),
      value: z.number().describe('Numeric value for the attribute'),
      unit: z.nativeEnum(UoMTypesEnum).describe('Unit of measurement (e.g., "h" for hours)'),
    })).optional().describe('Complete array of all variable attribute objects for the module. Should always have at least quantity attribute (id quantity in all languages)'),
    order: z.number().optional().describe('New order of the module in the quote section'),
  })).optional(),
  createQuoteItemRuledPrices: z.array(z.object({
    ruled_price_id: z.number().describe('ID of the ruled price to add'),
    quote_section_id: z.union([z.number(), z.string()]).describe('ID of the quote section to add the ruled price to. If the section was created in the same step, use the section\'s name'),
    margin: z.number().default(0.1).describe('Margin to apply to the ruled price (default: 0.1)'),
    data: z.array(z.object({
      id: z.string().describe('The UUID for the field. These IDs can be found in the fields of the simplified ruled price'),
      value: z.string().describe('The UUID of the option to select, can be found in the fields values options'),
      unit: z.nativeEnum(UoMTypesEnum).describe('Unit of measurement (e.g., "h" for hours, "u" for units)'),
    })).describe('Array of variable attribute objects for the ruled price. Must include all required variables from the ruled price configuration'),
    order: z.number().describe('Order of the ruled price in the quote section'),
  })).optional(),
  editQuoteItemRuledPrices: z.array(z.object({
    quote_item_ruled_price_id: z.number().describe('ID of the quote item ruled price to edit'),
    margin: z.number().optional().describe('New margin to apply to the ruled price'),
    data: z.array(z.object({
      id: z.string().describe('The UUID for the field. These IDs can be found in the fields of the simplified ruled price'),
      value: z.string().describe('The UUID of the option to select, can be found in the fields values options'),
      unit: z.nativeEnum(UoMTypesEnum).describe('Unit of measurement (e.g., "h" for hours, "u" for units)'),
    })).optional().describe('Complete array of all variable attribute objects for the ruled price. Must include all required variables from the ruled price configuration'),
    order: z.number().optional().describe('New order of the ruled price in the quote section'),
  })).optional(),
  // createQuoteItemSubcontractings: z.array(z.object({
  //   name: z.string().describe('Name of the subcontracting'),
  //   quote_section_id: z.union([z.number(), z.string()]).describe('ID of the quote section to add the subcontracting to. If the section was created in the same step, use the section\'s name'),
  //   margin: z.number().default(0.1).describe('Margin to apply to the subcontracting (default: 0.1)'),
  //   value: z.number().describe('Total price of the subcontracting'),
  //   order: z.number().describe('Order of the subcontracting in the quote section'),
  // })).optional(),
  editQuoteItemSubcontractings: z.array(z.object({
    quote_item_subcontracting_id: z.number().describe('ID of the quote item subcontracting to edit'),
    name: z.string().optional().describe('New name of the subcontracting'),
    margin: z.number().optional().describe('New margin to apply to the subcontracting'),
    value: z.number().optional().describe('New total price of the subcontracting'),
    order: z.number().optional().describe('New order of the subcontracting in the quote section'),
  })).optional(),
});

export const customizeModulesParameters = z.object({
  modules: z.array(z.object({
    id: z.number().describe('quote_item_module_id or linked_module_module_id to customize (NEVER the module ID or the parent but the quote_item_module_id/linked_module_module_id). For example if editing a choice in a module inside a quote_item_module, use the linked_module_module_id'),
    type: z.enum(['quote_item_module', 'linked_module_module']).describe('Type of the target quote_item_module or linked_module_module when nested in another module'),
    actions: z.object({

      edit: z.array(z.object({
        id: z.number().optional().describe('ID of the target linked_module_object_id, linked_module_module_id, module_ruled_price_link_id, or module_subcontracting_link_id'),
        type: z.enum(['linked_module_object', 'linked_module_module', 'module_ruled_price_link', 'module_subcontracting_link']).describe('Type of the target'),
        data: z.array(z.object({
          id: z.string().describe('The UUID for the field. For database objects, they are found in `quote_item_data_structure`. For ruled prices, it can be found in `fields`. For modules and subcontractings, it is just the quantity'),
          value: z.union([
            z.number(),
            z.string(),
          ]).describe('The number value of the field. For ruled prices only, they are string that represent the UUID of the option selected'),
          unit: z.nativeEnum(UoMTypesEnum),
        })).optional().describe('Complete array of all variable attribute objects. Must include all required variables from the configuration'),
        order: z.number().optional(),
      })).optional(),

      create: z.array(z.object({
        linked_item_id: z.number().describe('database_object_id, module_id, or ruled_price_id (-1 when creating a subcontracting)'),
        type: z.enum(['linked_module_object', 'linked_module_module', 'module_ruled_price_link', 'module_subcontracting_link']).describe('Type of the link to create'),
        data: z.array(z.object({
          id: z.string().describe('The UUID for the field. For database objects, they are found in `quote_item_data_structure`. For ruled prices, it can be found in `fields`. For modules and subcontractings, it is just the quantity'),
          value: z.union([
            z.number(),
            z.string(),
          ]).describe('The number value of the field. For ruled prices only, they are string that represent the UUID of the option selected'),
          unit: z.nativeEnum(UoMTypesEnum),
        })).optional().describe('Complete array of all variable attribute objects. Must include all required variables from the configuration. Quantity is mandatory'),
        name: z.string().optional().describe('Name of the subcontracting'),
        order: z.number(),
      })).optional(),

      delete: z.array(z.object({
        type: z.enum(['linked_object_module', 'linked_module_module', 'module_ruled_price_link', 'module_subcontracting_link']).describe('Type of the target'),
        id: z.number().describe('linked_object_module_id, linked_module_module_id, module_ruled_price_link_id, or module_subcontracting_link_id'),
      })).optional(),

      selectOptions: z.array(z.object({
        module_select_link_id: z.number().describe('ID of the module_select_link_id'),
        id: z.number().optional().describe('module_select_link_database_object_option_id, module_select_link_module_option_id, module_select_link_ruled_price_option_id, or module_select_link_subcontracting_option_id'),
      })).optional().describe('Array of module_select_links to select'),
    }).describe('All targets should be direct children of the specified quote item module or linked module module'),
  })),
});

export const makeModulesNonStandardParameters = z.object({
  modules: z.array(z.object({
    id: z.number(),
    type: z.enum(['quote_item_module', 'linked_module_module']),
  })),
}).describe('Array of modules to make non-standard, thus customizable. Only use on standard modules. When turning a standard module to non-standard, it\'s id change. Always refer to the new id when editing the module');
